import { useState, useMemo } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  Search,
  HelpCircle,
  BookOpen,
  Workflow,
  Brain,
  Camera,
  FileText,
  Users,
  Settings,
  ChevronRight,
  Lightbulb,
  Target,
  Scan,
  Palette,
  Ruler,
  Sun,
  Type,
  Clock,
  Wifi,
  CheckCircle,
  AlertTriangle,
  Image as ImageIcon,
  BarChart3,
  MessageCircle,
  Send,
  Download
} from "lucide-react";
import { API } from "../App";
import axios from "axios";

const LOGO_URL = "/axisindia-logo.webp";

// System workflow steps
const WORKFLOW_STEPS = [
  {
    id: "setup",
    title: "1. System Setup (Admin)",
    icon: Settings,
    description: "One-time configuration by the administrator",
    steps: [
      "Login with admin credentials (admin@verifier.com / admin123)",
      "Go to Product Types → create categories (e.g., Water Analyzer, Gas Analyzer)",
      "Go to Products → add products under each type. Set the Tolerance Configuration (defect sensitivity, flow threshold, mA tolerance etc.) and define any product-specific Test Input Fields (e.g., sensor count, channel configuration)",
      "Go to Phases → create verification phases (e.g., Physical Setup, Wiring, Functional)",
      "Go to Verification Steps → define each step with name, CV task type, pass criteria, and expected values. Use Conditional Steps to show/hide steps based on input field values"
    ],
    tip: "Each product has its own Tolerance Configuration that controls how strict the AI is. Set 'defect_sensitivity' to 'lenient' if dust is causing false failures."
  },
  {
    id: "finetuning",
    title: "2. AI Finetuning (Admin)",
    icon: Brain,
    description: "Upload reference images so the AI knows what PASS and FAIL look like",
    steps: [
      "Go to AI Finetuning → select Product → select Step",
      "Upload POSITIVE examples: photos of correctly assembled/working components. These teach the AI what GOOD looks like",
      "Upload NEGATIVE examples: photos of defects, wrong assembly, failures. These teach the AI what BAD looks like",
      "Use Bulk Upload to add multiple images at once — set label prefix and choose Positive or Negative",
      "Add clear descriptions (e.g., 'Sensor correctly seated in flow cell' or 'Sensor not inserted — loose on table')",
      "Images are auto-enhanced on upload: brightness corrected, sharpened, and auto-rotated"
    ],
    tip: "Upload at least 3-5 positive and 2-3 negative examples per step. The more reference images, the more accurate the AI. Check Analytics → 'AI Confidence' to see which steps need more references."
  },
  {
    id: "testing",
    title: "3. Running Verification Tests (QC Engineer)",
    icon: Camera,
    description: "Step-by-step verification of assembled products",
    steps: [
      "Login as QC Engineer (qc@verifier.com / qc123)",
      "Click 'Start New Test' → select Product → enter Serial Number, choose Power Supply (if applicable), and fill any product-specific input fields (e.g., sensor count, channel type)",
      "For each step: capture a photo using Camera or Upload from gallery",
      "The system checks image quality instantly (blur, dark, overexposed) — retake if needed",
      "Click 'Verify Step' → AI analyzes the image in background (15-30 seconds)",
      "Review the AI result: Pass (green), Fail (red), or Warning (yellow) with confidence %",
      "If AI is wrong: use Manual Accept, Manual Reject, or Skip Step with a comment",
      "If a step fails: fix the issue, then re-verify — the system keeps a retry history",
      "After all steps: click 'Complete Test' to finalize as Passed or Failed"
    ],
    tip: "Hold the phone steady and tap to focus before capturing. The system accepts photos in any orientation (portrait or landscape). For single-channel units, the AI only checks chlorine — it won't fail for missing pH/Turbidity."
  },
  {
    id: "reports",
    title: "4. Reports & Analytics",
    icon: FileText,
    description: "Track performance, identify trends, and generate compliance reports",
    steps: [
      "Test Reports: view all tests, filter by date/status, download PDF reports (Summary or Detailed with images)",
      "Analytics Dashboard: see pass rates, test volume trends, and per-product performance",
      "Failure Trends: see which steps fail most often (last 30 days) — focus improvement on high-fail-rate steps",
      "AI Confidence: see confidence score distribution — steps with low AI confidence need more reference images",
      "Export to Excel: download all test data as XLSX with 3 sheets (Test Summary, Step Details, Step Pass Rates)",
      "Compare Tests: select 2+ tests to compare step-by-step results side by side",
      "NOC Dashboard (Superadmin): view operation logs, step testing logs, system-wide metrics, and AI token usage per product/test"
    ],
    tip: "Export to Excel monthly for compliance records. Check Failure Trends weekly to catch assembly issues early."
  }
];

// CV Task Templates with detailed explanations
const CV_TASKS = [
  {
    id: "classification",
    name: "Classification",
    icon: Target,
    description: "Categorizes the image into predefined classes",
    useCase: "Verify product configuration matches selection (e.g., sensor count: 1, 2, or 3)",
    howItWorks: "AI examines the image and assigns it to one of the predefined categories based on visual features.",
    example: {
      scenario: "Sensor Configuration Check",
      setup: "Product has 3 variants: 1-sensor, 2-sensor, 3-sensor models",
      process: "Capture image of sensor array → AI counts sensors → Matches against app selection",
      passCriteria: "Detected sensor count matches the configuration selected in the app"
    },
    tips: [
      "Upload clear images of each class as reference",
      "Ensure consistent camera angle across all reference images",
      "Include edge cases in training data"
    ]
  },
  {
    id: "object_detection",
    name: "Object Detection",
    icon: Scan,
    description: "Locates and identifies specific objects within the image",
    useCase: "Find and verify presence of components like power modules, grounding wires",
    howItWorks: "AI scans the image to locate specific objects, returns bounding boxes and confidence scores for each detection.",
    example: {
      scenario: "Power Module Verification",
      setup: "Detect if 24W DC or 230W AC power module is installed",
      process: "Capture image of power area → AI detects module type → Confirms correct variant",
      passCriteria: "Correct power module type detected with >80% confidence"
    },
    tips: [
      "Provide multiple reference images from different angles",
      "Include images with partial obstructions",
      "Mark exact location in description"
    ]
  },
  {
    id: "anomaly_detection",
    name: "Anomaly Detection",
    icon: AlertTriangle,
    description: "Identifies defects, scratches, dents, or irregularities",
    useCase: "Quality inspection for physical damage on chassis or components",
    howItWorks: "AI compares image against 'normal' standards and highlights any deviations or defects.",
    example: {
      scenario: "Chassis Quality Check",
      setup: "Detect scratches, dents, or casting defects on analyzer body",
      process: "Capture chassis image → AI scans for anomalies → Reports defect locations",
      passCriteria: "No scratches deeper than 0.5mm, no visible dents or casting defects"
    },
    tips: [
      "Upload many 'good' examples to establish baseline",
      "Include negative examples of common defects",
      "Use consistent lighting to avoid false positives"
    ]
  },
  {
    id: "pattern_matching",
    name: "Pattern Matching",
    icon: Workflow,
    description: "Compares image against reference pattern for correct orientation/alignment",
    useCase: "Verify flow direction arrows, label orientations, connector positions",
    howItWorks: "AI matches visual patterns in the test image against reference images to verify correct alignment.",
    example: {
      scenario: "Flow Direction Arrow Check",
      setup: "Verify flow meter arrow points in correct direction",
      process: "Capture flow meter image → AI matches arrow pattern → Confirms direction",
      passCriteria: "Arrow direction matches reference (±5° tolerance)"
    },
    tips: [
      "Provide reference images with correct orientation clearly marked",
      "Include images of common mis-orientations as negative examples",
      "Document acceptable tolerance in pass criteria"
    ]
  },
  {
    id: "instance_counting",
    name: "Instance Counting",
    icon: BarChart3,
    description: "Counts the number of specific objects in the image",
    useCase: "Count cables, sensors, connectors, screws, terminals",
    howItWorks: "AI detects and counts individual instances of specified objects in the image.",
    example: {
      scenario: "Cable Entry Count",
      setup: "Count cables entering through glands (expected: 4 cables)",
      process: "Capture gland area → AI counts visible cables → Compares to specification",
      passCriteria: "Exactly 4 cables detected entering the enclosure"
    },
    tips: [
      "Ensure all items to count are visible in frame",
      "Use contrasting background when possible",
      "Document minimum visible percentage for partial items"
    ]
  },
  {
    id: "color_segmentation",
    name: "Color Segmentation",
    icon: Palette,
    description: "Identifies and segments regions by color",
    useCase: "Verify wire colors are in correct terminal positions",
    howItWorks: "AI analyzes color distribution and segments the image by distinct color regions.",
    example: {
      scenario: "Wire Color Verification",
      setup: "Verify Red (VCC), Black (GND), Green (Signal) wires in correct positions",
      process: "Capture terminal block → AI segments by color → Matches to standard",
      passCriteria: "Red wire in terminal 1, Black in terminal 2, Green in terminal 3"
    },
    tips: [
      "Use consistent lighting (color temperature affects detection)",
      "Document acceptable color variations",
      "Include images under different lighting conditions"
    ]
  },
  {
    id: "keypoint_detection",
    name: "Keypoint Detection",
    icon: Target,
    description: "Detects specific points of interest (terminals, connections)",
    useCase: "Verify wire seating, terminal connections, insertion points",
    howItWorks: "AI identifies precise locations of defined keypoints and verifies their positions.",
    example: {
      scenario: "Terminal Insertion Check",
      setup: "Verify all wires are fully seated in terminals with no exposed copper",
      process: "Capture terminal close-up → AI detects insertion points → Checks seating depth",
      passCriteria: "All wires fully inserted, no copper visible above terminal"
    },
    tips: [
      "Use macro/close-up images for precision",
      "Mark exact keypoint locations in reference images",
      "Define acceptable position tolerance"
    ]
  },
  {
    id: "geometric_analysis",
    name: "Geometric Analysis",
    icon: Ruler,
    description: "Measures angles, distances, compressions",
    useCase: "Verify gland tightness, seal compression, component alignment",
    howItWorks: "AI measures geometric properties like distances, angles, and compression ratios.",
    example: {
      scenario: "Gland Compression Check",
      setup: "Verify cable gland is properly tightened for IP65/67 rating",
      process: "Capture gland cross-section → AI measures compression → Compares to spec",
      passCriteria: "Gland compression between 15-20% for IP67 sealing"
    },
    tips: [
      "Include scale reference in image when possible",
      "Document measurement units in pass criteria",
      "Provide tolerance ranges for measurements"
    ]
  },
  {
    id: "luminance_analysis",
    name: "Luminance Analysis",
    icon: Sun,
    description: "Detects light emission, backlight status, LED indicators",
    useCase: "Verify LCD power-on, LED indicators, display backlight",
    howItWorks: "AI analyzes brightness levels and light emission patterns to detect operational status.",
    example: {
      scenario: "LCD Backlight Check",
      setup: "Verify LCD display is powered on and backlight is illuminated",
      process: "Capture display in dark room → AI measures luminance → Confirms backlight",
      passCriteria: "Display luminance >50 cd/m², even brightness distribution"
    },
    tips: [
      "Control ambient lighting for consistent results",
      "Define brightness thresholds clearly",
      "Test in similar conditions as reference images"
    ]
  },
  {
    id: "ocr",
    name: "OCR (Optical Character Recognition)",
    icon: Type,
    description: "Reads and extracts text from images",
    useCase: "Read serial numbers, model numbers, calibration labels, date codes",
    howItWorks: "AI extracts text from the image and can verify against expected values.",
    example: {
      scenario: "Serial Number Verification",
      setup: "Read serial number label and verify format matches WQA-YYYY-NNN",
      process: "Capture label → AI extracts text → Validates format and range",
      passCriteria: "Serial matches pattern WQA-2024-XXX where XXX is 001-999"
    },
    tips: [
      "Ensure text is in focus and readable",
      "Use high contrast between text and background",
      "Document expected text format/pattern"
    ]
  },
  {
    id: "temporal_analysis",
    name: "Temporal Analysis",
    icon: Clock,
    description: "Analyzes changes over time or sequences",
    useCase: "Verify boot sequences, startup animations, timed operations",
    howItWorks: "AI analyzes multiple frames or time-sequenced images to verify temporal patterns.",
    example: {
      scenario: "Boot Sequence Verification",
      setup: "Verify device completes boot sequence within 30 seconds",
      process: "Record boot → AI analyzes sequence → Confirms all stages complete",
      passCriteria: "All 5 boot stages complete in sequence within 30 seconds"
    },
    tips: [
      "Define clear start and end points",
      "Document expected sequence stages",
      "Include timing requirements in criteria"
    ]
  },
  {
    id: "api_call",
    name: "API Call / Data Verification",
    icon: Wifi,
    description: "Verifies data through API integration",
    useCase: "Validate calibration data, firmware version, configuration settings",
    howItWorks: "System queries device API or database to verify data matches specifications.",
    example: {
      scenario: "Firmware Version Check",
      setup: "Verify device is running firmware v2.5.0 or higher",
      process: "Query device API → Extract version → Compare to minimum required",
      passCriteria: "Firmware version >= 2.5.0"
    },
    tips: [
      "Document API endpoints and expected responses",
      "Include authentication requirements",
      "Define acceptable value ranges"
    ]
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "How do I add reference images for AI training?",
    answer: "Go to AI Finetuning → select Product → select Step → click 'Upload' tab. Upload POSITIVE images (correct assembly) and NEGATIVE images (defects). You can also use Bulk Upload to add many images at once with a label prefix. Images are auto-enhanced (brightness, sharpness, rotation) on upload."
  },
  {
    question: "Why did a test step fail even though the product looks correct?",
    answer: "Common causes: 1) Not enough reference images — add more positive examples, 2) Image too dark or blurry — check the quality badge before submitting, 3) AI compared date/serial against reference image literally — this is now fixed, 4) Dust on glass triggered defect detection — the system now ignores dust. If AI is wrong, use 'Manual Accept' to override with a comment."
  },
  {
    question: "Can I manually accept or reject a step?",
    answer: "Yes! After AI analysis, you'll see three buttons: Manual Accept (override to pass), Manual Reject (override to fail), and Skip Step. Each requires a comment explaining why. This is recorded in the test history for audit purposes."
  },
  {
    question: "What happens if I need to re-test a failed step?",
    answer: "Simply fix the issue, take a new photo, and click 'Re-verify'. The system saves all previous attempts in a Retry History — click the 'Attempt X' badge to see past results. This helps track how many tries each step needed."
  },
  {
    question: "Can I skip a step that doesn't apply to my product?",
    answer: "Yes. Click 'Skip Step' and enter a reason (e.g., 'No flow meter in this configuration'). Skipped steps count as passed for the overall test result. The skip reason is recorded in the report."
  },
  {
    question: "My photos keep failing because of image quality. What should I do?",
    answer: "The system now checks image quality BEFORE sending to AI. You'll see a badge: Green = Good, Yellow = Fair, Red = Poor. Tips: 1) Ensure good lighting, 2) Hold phone steady and tap to focus, 3) Avoid extreme close-ups, 4) Clean the camera lens. Poor quality images waste AI processing time."
  },
  {
    question: "How do I handle single-channel units (chlorine only)?",
    answer: "When you create a test session, set sensor count to 1. The AI automatically knows to check ONLY chlorine readings — it will NOT fail for missing pH or Turbidity. This applies to 4-20mA, calibration, and all sensor-related steps."
  },
  {
    question: "The AI says my enclosure glass has defects but it's just dust. How to fix?",
    answer: "This is already fixed. The AI now distinguishes between PERMANENT defects (scratches, cracks, dents) and temporary marks (dust, fingerprints). Only permanent damage causes a failure. If it still fails, use Manual Accept with a comment like 'Surface dust only, no permanent defects'."
  },
  {
    question: "How do I export test data for compliance reports?",
    answer: "Go to Analytics → click 'Export Excel'. This downloads a 3-sheet XLSX file: Test Summary (all tests with pass rates), Step Details (every step with AI confidence and notes), and Step Pass Rates (aggregated statistics per step). Use this for ISO audits, supplier meetings, and management reports."
  },
  {
    question: "How do I generate a PDF report?",
    answer: "Go to Test Reports → find your test → click the download icon. Choose 'Summary' (text only, smaller file) or 'Detailed' (includes all captured images). PDFs include QR codes and digital signatures for authenticity."
  },
  {
    question: "What does the AI Confidence percentage mean?",
    answer: "95-100% = AI is very sure of its result. 80-94% = Good confidence, minor uncertainty. 60-79% = Moderate — consider reviewing manually. Below 60% = AI is unsure — needs human review. Check Analytics → 'AI Confidence Distribution' to see which steps consistently have low confidence (they need more reference images)."
  },
  {
    question: "How does the Tolerance Configuration work?",
    answer: "Each product has tolerance settings that control AI strictness. Go to Products → Edit → see 'AI Tolerance Configuration'. Options: Defect Sensitivity (lenient/normal/strict), OCR Match Mode (presence/format/exact), Assembly Check (visual/strict), Flow Threshold (LPH), 4-20mA Tolerance, Calibration Tolerance %. Different products can have completely different tolerance levels."
  },
  {
    question: "The RS485 step keeps passing even without a cable connected. Why?",
    answer: "This is now fixed. The RS485 step requires TWO things in the photo: 1) The RS485 cable physically connected to the port, AND 2) The laptop screen showing communication data with no errors. If either is missing, it fails."
  },
  {
    question: "How long does each verification step take?",
    answer: "Approximately 15-30 seconds per step. The AI runs in the background — you'll see 'Analyzing...' with a spinner. If it takes longer than 2 minutes, the system will show a timeout message. The system auto-retries once if the first attempt fails."
  },
  {
    question: "What are the user roles?",
    answer: "Three roles: 1) Super Admin — full access including NOC dashboard and operation logs, 2) Admin — manages products, steps, users, finetuning, and reports, 3) QC Engineer — runs verification tests and views their own reports. Admins create QC Engineer accounts via User Management."
  },
  {
    question: "Can I upload photos in portrait (vertical) orientation?",
    answer: "Yes! The system accepts photos in ANY orientation — portrait, landscape, or rotated. Images are automatically corrected using EXIF rotation data before AI analysis. You don't need to worry about how you hold the phone."
  }
];

export default function HelpPage() {
  const { user, token } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [chatMessage, setChatMessage] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    { role: "assistant", content: "Hello! I'm here to help you with AxisIndia VerifyPro. Ask me anything about the system, CV tasks, or how to use specific features." }
  ]);

  // Filter content based on search
  const filteredTasks = useMemo(() => {
    if (!searchQuery) return CV_TASKS;
    const query = searchQuery.toLowerCase();
    return CV_TASKS.filter(task => 
      task.name.toLowerCase().includes(query) ||
      task.description.toLowerCase().includes(query) ||
      task.useCase.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const filteredFAQ = useMemo(() => {
    if (!searchQuery) return FAQ_ITEMS;
    const query = searchQuery.toLowerCase();
    return FAQ_ITEMS.filter(faq =>
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Offline fallback: keyword matcher. Returns a canned answer or null if nothing matches.
  const getKeywordResponse = (userMsg) => {
      let response = null;

      const lowerMsg = userMsg.toLowerCase();
      if (lowerMsg.includes("manual") || lowerMsg.includes("accept") || lowerMsg.includes("reject") || lowerMsg.includes("override")) {
        response = "After AI analyzes a step, you can: Manual Accept (pass it with a comment), Manual Reject (fail it), or Skip Step. Each action requires a comment explaining why. This is recorded in the audit trail.";
      } else if (lowerMsg.includes("retry") || lowerMsg.includes("re-verify") || lowerMsg.includes("re-test")) {
        response = "To retry a failed step: fix the issue, take a new photo, and click 'Re-verify'. The previous attempt is saved in Retry History. Click the 'Attempt X' badge to see all past attempts.";
      } else if (lowerMsg.includes("skip")) {
        response = "Click 'Skip Step' and enter a reason (e.g., 'No flow meter installed'). Skipped steps count as passed. The reason is recorded in reports for audit purposes.";
      } else if (lowerMsg.includes("quality") || lowerMsg.includes("blur") || lowerMsg.includes("dark")) {
        response = "The system checks image quality instantly after capture. Green badge = good quality. Red badge = poor (blurry, too dark, or overexposed). Tips: good lighting, hold steady, tap to focus, clean camera lens.";
      } else if (lowerMsg.includes("single") || lowerMsg.includes("chlorine only") || lowerMsg.includes("one channel")) {
        response = "For single-channel units: set sensor count to 1 when creating the test. The AI will ONLY check chlorine readings — it won't fail for missing pH or Turbidity on any step.";
      } else if (lowerMsg.includes("dust") || lowerMsg.includes("fingerprint") || lowerMsg.includes("enclosure")) {
        response = "The AI ignores normal dust and fingerprints on enclosure surfaces. It only fails for PERMANENT defects: scratches, cracks, chips, dents. If it still fails on dust, use Manual Accept.";
      } else if (lowerMsg.includes("tolerance") || lowerMsg.includes("sensitivity") || lowerMsg.includes("strict")) {
        response = "Each product has Tolerance Configuration: defect sensitivity (lenient/normal/strict), OCR match mode, assembly check level, flow threshold, mA tolerance. Edit these in Products page under 'AI Tolerance Configuration'.";
      } else if (lowerMsg.includes("speed") || lowerMsg.includes("slow") || lowerMsg.includes("time") || lowerMsg.includes("fast")) {
        response = "Each step takes ~15-30 seconds. The AI runs in background. Tips: ensure good image quality (avoids retakes), the system auto-retries once on AI failure. If consistently slow, check your internet connection.";
      } else if (lowerMsg.includes("excel") || lowerMsg.includes("export") || lowerMsg.includes("download")) {
        response = "Go to Analytics → click 'Export Excel'. Downloads 3-sheet XLSX: Test Summary, Step Details, Step Pass Rates. For PDF: go to Test Reports → click download icon on any test.";
      } else if (lowerMsg.includes("confidence") || lowerMsg.includes("accuracy")) {
        response = "Check Analytics → AI Confidence Distribution. It shows which steps have low confidence (need more reference images). Steps with >30% verifications below 70% confidence are flagged with 'Needs refs' badge.";
      } else if (lowerMsg.includes("reference") || lowerMsg.includes("training") || lowerMsg.includes("finetune")) {
        response = "Go to AI Finetuning → select Product → select Step. Upload POSITIVE images (correct assembly) and NEGATIVE images (defects). Use Bulk Upload for multiple images. Images are auto-enhanced on upload.";
      } else if (lowerMsg.includes("report") || lowerMsg.includes("pdf")) {
        response = "Test Reports: view/filter tests, download PDF (Summary or Detailed). Analytics: charts, failure trends, confidence distribution, Excel export. Compare Tests: side-by-side step comparison.";
      } else if (lowerMsg.includes("fail") || lowerMsg.includes("error") || lowerMsg.includes("wrong")) {
        response = "If AI gives wrong results: 1) Add more reference images in Finetuning, 2) Check image quality (blur/dark), 3) Use Manual Accept/Reject to override, 4) Adjust Tolerance Config in Products page.";
      } else if (lowerMsg.includes("user") || lowerMsg.includes("account") || lowerMsg.includes("login")) {
        response = "Roles: Super Admin (full access), Admin (manage products/steps/users), QC Engineer (run tests). Default logins: admin@axisindia.in, test_qc@axisindia.in, superadmin@axisindia.in — all with password admin123.";
      } else if (lowerMsg.includes("ocr") || lowerMsg.includes("text") || lowerMsg.includes("serial") || lowerMsg.includes("number")) {
        response = "OCR reads text from images (serial numbers, display values, labels). Each unit has a UNIQUE serial number — the AI verifies the format is correct, not that it matches the reference image's number.";
      } else if (lowerMsg.includes("rs485") || lowerMsg.includes("communication") || lowerMsg.includes("modbus")) {
        response = "RS485 step requires a photo showing BOTH: the cable connected to the RS485 port AND the laptop screen with communication data (no errors). Fails if cable is disconnected or errors are visible.";
      } else if (lowerMsg.includes("rotameter") || lowerMsg.includes("flow")) {
        response = "Rotameter step reads the flow rate value. It MUST be above the threshold (default 20 LPH). If rotameter is closed/stopped, the step fails. The threshold is configurable in product Tolerance Configuration.";
      } else if (lowerMsg.includes("orientation") || lowerMsg.includes("vertical") || lowerMsg.includes("horizontal") || lowerMsg.includes("rotate")) {
        response = "Photos are accepted in ANY orientation — portrait, landscape, or rotated. The system auto-corrects rotation using EXIF data. Focus on getting the right content in the frame, not how you hold the phone.";
      }

      return response;
  };

  // AI-backed chat handler. Calls the Gemini-backed help endpoint; falls back to the
  // offline keyword matcher if the AI is unavailable.
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim() || chatLoading) return;

    const userMsg = chatMessage.trim();
    const priorHistory = chatHistory;
    setChatHistory(prev => [...prev, { role: "user", content: userMsg }]);
    setChatMessage("");
    setChatLoading(true);

    try {
      const res = await axios.post(
        `${API}/help/chat`,
        { message: userMsg, history: priorHistory.slice(-8) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const reply = res?.data?.reply?.trim();
      if (reply) {
        setChatHistory(prev => [...prev, { role: "assistant", content: reply }]);
      } else {
        throw new Error("Empty reply");
      }
    } catch (err) {
      // Fall back to the offline keyword matcher so the chat still helps if the AI is down.
      const fallback = getKeywordResponse(userMsg)
        || "I'm having trouble reaching the AI assistant right now. Try searching the documentation above or check the FAQ section — and please try again in a moment.";
      setChatHistory(prev => [...prev, { role: "assistant", content: fallback }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="help-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <HelpCircle className="w-7 h-7 text-blue-500" />
            Help Center
          </h2>
          <p className="text-muted-foreground">
            System documentation, CV task guides, and FAQs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => { localStorage.removeItem("walkthrough_seen"); window.location.reload(); }}>
            <BookOpen className="w-4 h-4 mr-2" />
            Start Walkthrough
          </Button>
          <img src={LOGO_URL} alt="AxisIndia" className="h-10 w-auto" />
        </div>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search help topics, CV tasks, FAQs..."
              className="pl-10 text-lg h-12"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="help-search"
            />
          </div>
          {searchQuery && (
            <p className="text-sm text-muted-foreground mt-2">
              Found {filteredTasks.length} CV tasks and {filteredFAQ.length} FAQ items
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="workflow" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="workflow" data-testid="tab-workflow">
            <Workflow className="w-4 h-4 mr-2" />
            Workflow
          </TabsTrigger>
          <TabsTrigger value="cv-tasks" data-testid="tab-cv-tasks">
            <Brain className="w-4 h-4 mr-2" />
            CV Tasks
          </TabsTrigger>
          <TabsTrigger value="faq" data-testid="tab-faq">
            <BookOpen className="w-4 h-4 mr-2" />
            FAQ
          </TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat">
            <MessageCircle className="w-4 h-4 mr-2" />
            Ask Help
          </TabsTrigger>
        </TabsList>

        {/* Workflow Tab */}
        <TabsContent value="workflow" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {WORKFLOW_STEPS.map((step, index) => (
              <Card key={step.id} className="relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-blue-600" />
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <step.icon className="w-5 h-5 text-blue-500" />
                    </div>
                    {step.title}
                  </CardTitle>
                  <CardDescription>{step.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ol className="space-y-2">
                    {step.steps.map((s, i) => (
                      <li key={`${step.title}-step-${i}`} className="flex items-start gap-2 text-sm">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium">
                          {i + 1}
                        </span>
                        {s}
                      </li>
                    ))}
                  </ol>
                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">{step.tip}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* CV Tasks Tab */}
        <TabsContent value="cv-tasks" className="space-y-4">
          <p className="text-muted-foreground">
            Detailed explanations of each Computer Vision task type available in VerifyPro.
          </p>
          
          <Accordion type="single" collapsible className="space-y-2">
            {filteredTasks.map((task) => (
              <AccordionItem key={task.id} value={task.id} className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <task.icon className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold">{task.name}</p>
                      <p className="text-sm text-muted-foreground font-normal">{task.description}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6">
                  <div className="space-y-6 pl-12">
                    {/* Use Case */}
                    <div>
                      <h4 className="font-semibold text-sm text-slate-700 mb-1">Use Case</h4>
                      <p className="text-sm">{task.useCase}</p>
                    </div>

                    {/* How It Works */}
                    <div>
                      <h4 className="font-semibold text-sm text-slate-700 mb-1">How It Works</h4>
                      <p className="text-sm">{task.howItWorks}</p>
                    </div>

                    {/* Example */}
                    <Card className="bg-slate-50 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Example: {task.example.scenario}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <Badge variant="outline" className="mb-1">Setup</Badge>
                          <p className="text-sm">{task.example.setup}</p>
                        </div>
                        <div>
                          <Badge variant="outline" className="mb-1">Process</Badge>
                          <p className="text-sm">{task.example.process}</p>
                        </div>
                        <div>
                          <Badge variant="outline" className="mb-1 bg-green-50 text-green-700 border-green-200">Pass Criteria</Badge>
                          <p className="text-sm font-medium text-green-700">{task.example.passCriteria}</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Tips */}
                    <div>
                      <h4 className="font-semibold text-sm text-slate-700 mb-2">Tips for Best Results</h4>
                      <ul className="space-y-1">
                        {task.tips.map((tip, i) => (
                          <li key={`${task.title}-tip-${i}`} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>

        {/* FAQ Tab */}
        <TabsContent value="faq" className="space-y-4">
          <p className="text-muted-foreground">
            Frequently asked questions about using VerifyPro.
          </p>
          
          <Accordion type="single" collapsible className="space-y-2">
            {filteredFAQ.map((faq, index) => (
              <AccordionItem key={`faq-${faq.question.slice(0, 20)}-${index}`} value={`faq-${index}`} className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline text-left">
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4 pl-8">
                  <p className="text-sm text-slate-600">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>

        {/* Chat Tab */}
        <TabsContent value="chat">
          <Card className="h-[500px] flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-500" />
                Help Assistant
              </CardTitle>
              <CardDescription>
                Ask questions about VerifyPro features and get instant answers
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <ScrollArea className="flex-1 pr-4 mb-4">
                <div className="space-y-4">
                  {chatHistory.map((msg, index) => (
                    <div
                      key={`chat-${msg.role}-${index}`}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === "user"
                            ? "bg-blue-500 text-white"
                            : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] p-3 rounded-lg bg-slate-100 text-slate-500">
                        <p className="text-sm italic">Assistant is typing…</p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>

              {chatHistory.length <= 1 && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-2">Suggested questions:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "How do I manually accept a step?",
                      "My image is too dark",
                      "How to handle single channel units?",
                      "How to skip a step?",
                      "How to export to Excel?",
                      "Enclosure fails on dust",
                      "How to retry a failed step?",
                      "What does AI confidence mean?",
                      "RS485 keeps passing without cable",
                      "How to change tolerance settings?",
                      "How fast is each step?",
                      "Can I upload vertical photos?",
                    ].map((q) => (
                      <Button
                        key={q}
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setChatMessage(q)}
                      >
                        {q}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleChatSubmit} className="flex gap-2">
                <Input
                  placeholder="Type your question..."
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  className="flex-1"
                  disabled={chatLoading}
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={chatLoading || !chatMessage.trim()} data-testid="chat-send">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
